import { useEffect, useRef } from 'react';
import { useTranslation } from '../../lib/i18n';
import type { ConnectionState, RetryController, RetryEpisode, RetryPolicy, RetryProgress, RetryPhase, RetryReason } from 'elowen-plugin-shared/retry';
import type { TranscriptEvent as BrainEvent } from '../../lib/transcript';
import { BASE, elowenClient } from '../../lib/elowenClient';
import { createReconnectController } from '../../lib/reconnect';
import { readDaemonStream, type DaemonStreamFrame } from '../../lib/daemonStream';
import { startStreamWatchdog } from '../../lib/streamWatchdog';
import { STALE_HIDE_MS, type ReviveEvent } from '../../lib/useRevive';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from '../../lib/token';
import { CHAT_HISTORY_PAGE_SIZE } from '../../lib/chatHistory';
import type { AskQuestion, BrainCard, BrainGoal, BrainStreamSnapshotFrame, BrainUsage, ProcessInfo } from '../../lib/types';
import type { WorkflowState } from '../../lib/transcript';
import type { BrainWorkMode, ChatEventView } from '../../../src/shared/wireContract';

const SNAPSHOT_TIMEOUT_MS = 15_000;

type AskFrame = { id: string; questions: AskQuestion[]; kind?: 'approval' };
type SubagentFrame = { id: string; sessionId: string; status: 'running' | 'done' | 'error'; task: string; detail?: string; tools: number; tokens?: number; seconds: number; model?: string };
type WorkflowFrame = { id: string; toolCallId: string; title?: string; status: WorkflowState['status']; nodes: WorkflowState['nodes'] };

interface SessionStreamHandlers {
  event: (event: BrainEvent) => void;
  snapshot: (snapshot: BrainStreamSnapshotFrame) => void;
  text?: (delta: string) => void;
  notice?: (message: string, done?: boolean) => void;
  error?: (message: string) => void;
  session?: (sessionId: string) => void;
  subagent?: (frame: SubagentFrame) => void;
  workflow?: (frame: WorkflowFrame) => void;
  goal?: (goal: BrainGoal | null) => void;
  promptSuggestion?: (text: string | null) => void;
  title?: () => void;
  settings?: () => void;
  process?: (processes: ProcessInfo[]) => void;
  card?: (card: BrainCard) => void;
  queue?: (items: { id: string; text: string }[]) => void;
  discardUser?: (frame: { durableId: string; text: string }) => void;
  compacted?: (mode: BrainWorkMode) => void;
  /** Stored history changed outside the current turn. Reconnect so one atomic snapshot carries the
   *  committed rows together with any reply still running — this frame can arrive mid-turn. */
  resync?: () => void;
  chatEvent?: (event: ChatEventView) => void;
  ask?: (frame: AskFrame) => void;
  askResolved?: (id: string) => void;
  step?: (usage?: BrainUsage) => void;
  idle?: (usage?: BrainUsage, activitySeq?: number) => void;
}

interface LiveStreamHandlers extends SessionStreamHandlers {
  snapshotStart: () => void;
}
interface ReadOnlyStreamHandlers extends SessionStreamHandlers {
  openError: () => void;
}
interface BrainChatStreamOptions {
  getGeneration: () => number;
  loadPolicy: (signal: AbortSignal) => Promise<RetryPolicy>;
  getPolicy: () => RetryPolicy;
  setReady: (ready: boolean) => void;
  setReconnecting: (active: boolean) => void;
  /** `firstAttempt` is true while the current outage is still on its first attempt (running or about to
   *  run): a recovery that succeeds there is not worth announcing. */
  onConnectionState: (state: ConnectionState, firstAttempt: boolean) => void;
  onFailure: (source: 'policy' | 'stream', error: unknown) => void;
}

type LiveBinding = { generation: number; session: string; client: string; boundGeneration: number | undefined; handlers: LiveStreamHandlers };
type ReadOnlyBinding = { generation: number; session: string; handlers: ReadOnlyStreamHandlers };
type StreamBinding = {
  generation: number;
  session: string;
  issuedIn: number;
  lifetime: AbortController;
  controller: RetryController<void> | null;
  opening: Promise<void> | null;
  interrupt: ((reason: RetryReason) => void) | null;
  url: string;
  hydrated: boolean;
  phase: RetryPhase | null;
  /** The retry number at which the controller last reported this episode connected; an outage counts its
   *  attempts from there. A new episode (restart) has none, so its attempt 0 is the outage's first. */
  connectedAt: { episodeId: string; retryNumber: number } | null;
} & ({ kind: 'live'; handlers: LiveStreamHandlers; client: string } | { kind: 'read-only'; handlers: ReadOnlyStreamHandlers });

export interface BrainChatStream {
  close: () => void;
  openLive: (options: LiveBinding) => Promise<void>;
  openReadOnly: (options: ReadOnlyBinding) => Promise<void>;
  /** Automatic resnapshot: ends only this GET attempt, preserving the finite episode. */
  reconnectNow: () => void;
  /** Explicit user retry; never replays start/send or changes the conversation binding. */
  retry: () => void;
  revive: (event: ReviveEvent, attached: boolean, reviveLimitMs: number) => void;
  watch: (attached: () => boolean, limitMs: number) => () => void;
  stop: () => void;
}

export function useBrainChatStream(options: BrainChatStreamOptions): BrainChatStream {
  const { t } = useTranslation();
  const activityText = useRef(t.brainChat);
  activityText.current = t.brainChat;
  const latest = useRef(options);
  latest.current = options;
  const bindingRef = useRef<StreamBinding | null>(null);
  const cleanupRef = useRef<Promise<void>>(Promise.resolve());
  const lastFrameAtRef = useRef(0);

  const current = (binding: StreamBinding): boolean => bindingRef.current === binding
    && !binding.lifetime.signal.aborted && binding.generation === latest.current.getGeneration()
    && binding.issuedIn === authGeneration();

  const close = (): void => {
    const binding = bindingRef.current;
    bindingRef.current = null;
    if (binding) {
      binding.lifetime.abort();
      cleanupRef.current = Promise.all([cleanupRef.current, binding.opening, binding.controller?.stop()]).then(() => undefined);
    }
    latest.current.setReconnecting(false);
    latest.current.setReady(false);
  };

  useEffect(() => {
    const off = subscribeAuthTransitions(transition => {
      if (!transition.refresh) { close(); return; }
      if (transition.phase === 'start') {
        // Keep the fixed GET target and its handlers, but fence every old reader callback.
        const binding = bindingRef.current;
        close();
        bindingRef.current = binding;
      } else if (transition.phase === 'ready') {
        const previous = bindingRef.current;
        if (!previous) return;
        const binding: StreamBinding = {
          ...previous, issuedIn: authGeneration(), lifetime: new AbortController(),
          controller: null, opening: null, interrupt: null, phase: null, connectedAt: null,
        };
        bindingRef.current = binding;
        void openController(binding);
      }
    });
    window.addEventListener(AUTH_CLEARED_EVENT, close);
    return () => {
      off();
      window.removeEventListener(AUTH_CLEARED_EVENT, close);
      close();
    };
    // The subscription owns this hook's lifetime; methods read current options through latest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = (frame: DaemonStreamFrame, handlers: SessionStreamHandlers): void => {
    const event = { ...JSON.parse(frame.data), type: frame.event } as BrainEvent;
    handlers.event(event);
    switch (event.type) {
      case 'text': handlers.text?.(event.delta); break;
      case 'notice': handlers.notice?.(event.kind === 'compaction' && !event.done ? activityText.current.compacting : event.message, event.done); break;
      case 'session': handlers.session?.(event.sessionId); break;
      case 'subagent': handlers.subagent?.(event); break;
      case 'workflow': handlers.workflow?.(event); break;
      case 'goal': handlers.goal?.(event.goal); break;
      case 'prompt-suggestion': handlers.promptSuggestion?.(event.text); break;
      case 'title': handlers.title?.(); break;
      case 'settings': handlers.settings?.(); break;
      case 'process': handlers.process?.(event.processes); break;
      case 'card': handlers.card?.(event.card); break;
      case 'queue': handlers.queue?.(event.items); break;
      case 'discard_user': handlers.discardUser?.(event); break;
      case 'compacted': handlers.compacted?.(event.workMode); break;
      case 'resync': handlers.resync?.(); break;
      case 'chat-event': handlers.chatEvent?.(event.event); break;
      case 'ask': handlers.ask?.(event); break;
      case 'ask_resolved': handlers.askResolved?.(event.id); break;
      case 'step': handlers.step?.(event.usage); break;
      case 'idle': handlers.idle?.(event.usage, event.activitySeq); break;
      case 'error': case 'reasoning': case 'tool_authoring': case 'tool': case 'diff':
      case 'tool_output': case 'tool_end': case 'tool_progress': case 'image': case 'file': case 'view': case 'blocks':
      case 'typing': case 'control': case 'retry': case 'delegation': case 'delivery': case 'user': case 'turn-boundary': case 'message-stored': case 'status-changed': break;
    }
  };

  const observe = (binding: StreamBinding, episode: RetryEpisode<void>): void => {
    void episode.done.catch(error => {
      if (current(binding)) latest.current.onFailure('stream', error);
    });
  };

  const openController = (binding: StreamBinding, restart = false): Promise<void> => {
    if (binding.opening) return binding.opening;
    const opening = (async () => {
      await cleanupRef.current;
      if (!current(binding)) return;
      try {
        await latest.current.loadPolicy(binding.lifetime.signal);
      } catch (error) {
        if (current(binding)) latest.current.onFailure('policy', error);
        return;
      }
      if (!current(binding)) return;
      if (binding.controller) {
        if (restart) observe(binding, binding.controller.restart());
        return;
      }
      binding.controller = createReconnectController<void>({
        target: { kind: 'chat', id: binding.session },
        getPolicy: () => latest.current.getPolicy(),
        signal: binding.lifetime.signal,
        onProgress: (progress: RetryProgress) => {
          if (!current(binding)) return;
          binding.phase = progress.phase;
          const ready = progress.phase === 'succeeded';
          const { retryNumber } = progress;
          // The shared controller always counts its own retries; only native progress may lack a number.
          if (retryNumber === null) throw new TypeError('Chat stream retry progress has no retry number');
          if (ready) binding.connectedAt = { episodeId: progress.episodeId, retryNumber };
          const base = binding.connectedAt?.episodeId === progress.episodeId ? binding.connectedAt.retryNumber : -1;
          // A scheduled phase already carries the number of the attempt it waits for.
          const firstAttempt = (progress.phase === 'attempting' || progress.phase === 'scheduled') && retryNumber - base <= 1;
          latest.current.setReconnecting(!ready && (binding.hydrated || progress.retryNumber !== 0)
            && (progress.phase === 'attempting' || progress.phase === 'scheduled'));
          if (!ready) latest.current.setReady(false);
          latest.current.onConnectionState({
            target: progress.target, generation: progress.generation, sequence: progress.sequence,
            transport: ready ? 'connected' : progress.phase === 'attempting' ? 'connecting' : 'disconnected',
            readiness: ready ? 'ready' : 'pending', retry: progress, observedAt: Date.now(),
            authority: { kind: 'local-owner', id: `web:${binding.session}:${binding.generation}` }, freshness: 'fresh',
          }, firstAttempt);
        },
        attempt: async context => {
          if (!current(binding)) return { outcome: 'cancelled', reason: 'aborted' };
          const attempt = new AbortController();
          const signal = AbortSignal.any([context.signal, attempt.signal]);
          let interrupted: RetryReason | null = null;
          const interrupt = (reason: RetryReason): void => { interrupted = reason; attempt.abort(); };
          binding.interrupt = interrupt;
          lastFrameAtRef.current = Date.now();
          let snapshotSeen = false;
          let unavailable = false;
          const timer = setTimeout(() => interrupt('timeout'), SNAPSHOT_TIMEOUT_MS);
          if (binding.kind === 'live') {
            elowenClient.brainVisibility({ client: binding.client, hidden: document.hidden });
          }
          try {
            const result = await readDaemonStream(binding.url, {
              signal,
              onBytes: () => { if (current(binding) && !signal.aborted) lastFrameAtRef.current = Date.now(); },
              onFrame: frame => {
                if (!current(binding) || signal.aborted) return;
                if (frame.event === 'heartbeat') return;
                if (frame.event === 'snapshot') {
                  if (binding.kind === 'live') binding.handlers.snapshotStart();
                  binding.handlers.snapshot(JSON.parse(frame.data) as BrainStreamSnapshotFrame);
                  if (!current(binding) || signal.aborted) return;
                  snapshotSeen = true;
                  clearTimeout(timer);
                  binding.hydrated = true;
                  context.ready();
                  latest.current.setReady(true);
                  return;
                }
                if (frame.event === 'error') {
                  if (binding.kind === 'read-only' && !snapshotSeen) {
                    unavailable = true;
                    binding.handlers.openError();
                    interrupt('not_ready');
                    return;
                  }
                  const event = { ...JSON.parse(frame.data), type: 'error' } as Extract<BrainEvent, { type: 'error' }>;
                  binding.handlers.event(event);
                  binding.handlers.error?.(event.message);
                  if (binding.kind === 'live') interrupt('unavailable');
                  return;
                }
                dispatch(frame, binding.handlers);
                if (binding.kind === 'read-only' && (frame.event === 'compacted' || frame.event === 'resync')) interrupt('not_ready');
              },
            });
            if (context.signal.aborted || !current(binding)) return { outcome: 'cancelled', reason: 'aborted' };
            if (unavailable) return { outcome: 'blocked', reason: 'unavailable' };
            if (interrupted) return { outcome: 'retry', reason: interrupted };
            if (result.outcome === 'blocked' && binding.kind === 'read-only' && !snapshotSeen) binding.handlers.openError();
            return result;
          } finally {
            clearTimeout(timer);
            if (binding.interrupt === interrupt) binding.interrupt = null;
          }
        },
      });
      observe(binding, binding.controller.start());
    })().catch(error => {
      if (current(binding)) latest.current.onFailure('stream', error);
    });
    binding.opening = opening;
    void opening.then(() => { if (binding.opening === opening) binding.opening = null; });
    return opening;
  };

  const retry = (): void => {
    const binding = bindingRef.current;
    if (!binding || !current(binding)) return;
    void openController(binding, true);
  };

  const reconnectNow = (): void => {
    const binding = bindingRef.current;
    if (!binding || !current(binding)) return;
    binding.interrupt?.('timeout');
    binding.controller?.start();
  };

  return {
    close,
    openLive: ({ generation, session, client, boundGeneration, handlers }) => {
      close();
      const params = new URLSearchParams({ session, client, generation: String(boundGeneration), surface: 'web', snapshot: '1', history: String(CHAT_HISTORY_PAGE_SIZE), heartbeat: '1' });
      const binding: StreamBinding = {
        kind: 'live', generation, session, client, handlers, issuedIn: authGeneration(), lifetime: new AbortController(),
        controller: null, opening: null, interrupt: null, url: `${BASE}/brain/stream?${params}`, hydrated: false, phase: null, connectedAt: null,
      };
      bindingRef.current = binding;
      return openController(binding);
    },
    openReadOnly: ({ generation, session, handlers }) => {
      close();
      // Read-only child taps intentionally carry no parent client/generation authority.
      const params = new URLSearchParams({ session, surface: 'web', snapshot: '1', heartbeat: '1' });
      const binding: StreamBinding = {
        kind: 'read-only', generation, session, handlers, issuedIn: authGeneration(), lifetime: new AbortController(),
        controller: null, opening: null, interrupt: null, url: `${BASE}/brain/stream?${params}`, hydrated: false, phase: null, connectedAt: null,
      };
      bindingRef.current = binding;
      return openController(binding);
    },
    reconnectNow,
    retry,
    revive: ({ hiddenMs, networkRecovered, foregroundReturned }, attached, reviveLimitMs) => {
      if (!attached) return;
      const stale = hiddenMs > STALE_HIDE_MS || Date.now() - lastFrameAtRef.current > reviveLimitMs;
      const phase = bindingRef.current?.phase;
      const renewable = phase === 'exhausted' || (stale && (phase === 'attempting' || phase === 'scheduled' || phase === 'succeeded'));
      if (networkRecovered || (foregroundReturned && renewable)) {
        retry();
        return;
      }
      if (stale) reconnectNow();
    },
    watch: (attached, limitMs) => startStreamWatchdog({
      lastFrameAt: () => lastFrameAtRef.current, limitMs,
      onSilent: () => { if (attached()) reconnectNow(); },
    }),
    stop: close,
  };
}
